import { capitalCase } from 'change-case';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Link, Paper, Stack, Tooltip, Typography } from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
// components
import Page from '../../components/Page';
import Logo from '../../components/Logo';
import Image from '../../components/Image';
// sections
import { LoginForm } from '../../sections/auth/login';
import { BUSINESS_NAME } from '../../config';
import KineticGrid from '../../components/ui/kinetic-grid';

// ----------------------------------------------------------------------

const BRAND = {
  pink: '#EC1467',
  yellow: '#FFD400',
  brown: '#43291A',
  ink: '#14151A',
  muted: '#8A8F98',
  mist: '#F7F7F9',
};

const RootStyle = styled('div')({
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'clamp(16px, 4vw, 56px)',
});

const CardStyle = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 1220,
  minHeight: 720,
  display: 'flex',
  overflow: 'hidden',
  borderRadius: 28,
  backgroundColor: '#FFFFFF',
  boxShadow: '0 28px 90px rgba(0, 0, 0, 0.34)',
  [theme.breakpoints.down('md')]: {
    minHeight: 'auto',
    borderRadius: 22,
  },
}));

const BrandPanelStyle = styled('section')(({ theme }) => ({
  width: '42%',
  minHeight: 'auto',
  display: 'none',
  position: 'relative',
  overflow: 'hidden',
  flexShrink: 0,
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(5, 6),
  backgroundColor: BRAND.mist,
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(6),
  },
}));

const FormPanelStyle = styled('main')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(7, 8),
  },
}));

// ----------------------------------------------------------------------

function BubbleCluster() {
  const bubbles = [
    { cx: 120, cy: 640, r: 70, fill: BRAND.pink, opacity: 0.08 },
    { cx: 260, cy: 520, r: 30, fill: BRAND.pink, opacity: 0.14 },
    { cx: 70, cy: 430, r: 14, fill: BRAND.yellow, opacity: 0.9 },
    { cx: 200, cy: 360, r: 46, fill: BRAND.pink, opacity: 0.1 },
    { cx: 300, cy: 260, r: 8, fill: BRAND.brown, opacity: 0.12 },
    { cx: 130, cy: 190, r: 22, fill: BRAND.pink, opacity: 0.16 },
    { cx: 250, cy: 110, r: 12, fill: BRAND.yellow, opacity: 0.7 },
    { cx: 60, cy: 90, r: 5, fill: BRAND.brown, opacity: 0.15 },
  ];

  return (
    <Box
      component='svg'
      viewBox='0 0 380 720'
      preserveAspectRatio='xMidYMid slice'
      aria-hidden='true'
      focusable='false'
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      {bubbles.map((bubble, index) => (
        <circle key={`${bubble.cx}-${bubble.cy}-${index}`} {...bubble} />
      ))}
    </Box>
  );
}

// ----------------------------------------------------------------------

export default function Login() {
  const { method } = useAuth();

  return (
    <Page title='Login'>
      <KineticGrid
        theme={{
          background: '#150C14',
          lineColor: '#9C5578',
          lineOpacity: 0.33,
          dotColor: '#EC1E79',
          warpGlow: '#EC1E79',
          rippleColor: '#FFD400',
        }}
      >
        <RootStyle>
          <CardStyle elevation={0}>
            <BrandPanelStyle aria-label={`${BUSINESS_NAME} welcome`}>
          <BubbleCluster />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Logo sx={{ width: 176, height: 176 }} />
          </Box>

          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 360 }}>
            <Typography
              component='h1'
              sx={{
                color: BRAND.ink,
                fontSize: { md: 32, lg: 38 },
                fontWeight: 700,
                lineHeight: 1.16,
                letterSpacing: '-0.035em',
              }}
            >
              Welcome back.
              <br />
              Let&apos;s get moving.
            </Typography>
            <Typography sx={{ mt: 2, color: BRAND.muted, fontSize: 15, lineHeight: 1.7 }}>
              Sign in to manage your {BUSINESS_NAME} account, orders, and everything in between.
            </Typography>
          </Box>
            </BrandPanelStyle>

            <FormPanelStyle>
          <Box sx={{ width: '100%', maxWidth: 420 }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}>
              <Logo sx={{ width: 124, height: 124 }} />
            </Stack>

            <Typography sx={{ mb: { xs: 4, sm: 5 }, color: BRAND.muted, fontSize: 13, textAlign: { xs: 'left', sm: 'right' } }}>
              Don&apos;t have an account?{' '}
              <Link component={RouterLink} to='mailto:info@buyzone.com' sx={{ color: BRAND.pink, fontWeight: 600, textDecoration: 'none' }}>
                Contact the team
              </Link>
            </Typography>

            <Stack direction='row' alignItems='flex-start' spacing={2} sx={{ mb: 4.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography component='h2' sx={{ color: BRAND.ink, fontSize: { xs: 27, sm: 30 }, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.025em' }}>
                  Sign in to {BUSINESS_NAME}
                </Typography>
                <Typography sx={{ mt: 1, color: BRAND.muted, fontSize: 14.5 }}>Enter your credentials to continue.</Typography>
              </Box>

              <Tooltip title={capitalCase(method)} placement='right'>
                <Box sx={{ mt: 0.25, p: 0.75, lineHeight: 0, border: '1px solid #E7E7EC', borderRadius: 1.5 }}>
                  <Image disabledEffect src={`https://minimal-assets-api.vercel.app/assets/icons/auth/ic_${method}.png`} sx={{ width: 28, height: 28 }} />
                </Box>
              </Tooltip>
            </Stack>

            <LoginForm />
          </Box>
            </FormPanelStyle>
          </CardStyle>
        </RootStyle>
      </KineticGrid>
    </Page>
  );
}

// ----------------------------------------------------------------------
// LEGACY LOGIN PAGE (pre-redesign)
// Rollback: comment out the active implementation above, then uncomment
// every line in this legacy block.
// ----------------------------------------------------------------------
// import { capitalCase } from 'change-case';
// import { Link as RouterLink } from 'react-router-dom';
// // @mui
// import { styled } from '@mui/material/styles';
// import { Box, Card, Container, Link, Stack, Tooltip, Typography } from '@mui/material';
// // hooks
// import useAuth from '../../hooks/useAuth';
// import useResponsive from '../../hooks/useResponsive';
// // components
// import Page from '../../components/Page';
// import Logo from '../../components/Logo';
// import Image from '../../components/Image';
// // sections
// import { LoginForm } from '../../sections/auth/login';
// import { BUSINESS_NAME } from '../../config';
//
// // ----------------------------------------------------------------------
//
// const RootStyle = styled('div')(({ theme }) => ({
//   [theme.breakpoints.up('md')]: {
//     display: 'flex',
//   },
// }));
//
// const HeaderStyle = styled('header')(({ theme }) => ({
//   top: 0,
//   zIndex: 9,
//   lineHeight: 0,
//   width: '100%',
//   display: 'flex',
//   alignItems: 'center',
//   position: 'absolute',
//   padding: theme.spacing(3),
//   justifyContent: 'space-between',
//   [theme.breakpoints.up('md')]: {
//     alignItems: 'flex-start',
//     padding: theme.spacing(7, 5, 0, 7),
//   },
// }));
//
// const SectionStyle = styled(Card)(({ theme }) => ({
//   width: '100%',
//   maxWidth: 464,
//   display: 'flex',
//   flexDirection: 'column',
//   justifyContent: 'center',
//   margin: theme.spacing(2, 0, 2, 2),
// }));
//
// const ContentStyle = styled('div')(({ theme }) => ({
//   maxWidth: 480,
//   margin: 'auto',
//   display: 'flex',
//   minHeight: '100vh',
//   flexDirection: 'column',
//   justifyContent: 'center',
//   padding: theme.spacing(12, 0),
// }));
//
// // ----------------------------------------------------------------------
//
// export default function Login() {
//   const { method } = useAuth();
//
//   const smUp = useResponsive('up', 'sm');
//
//   const mdUp = useResponsive('up', 'md');
//
//   return (
//     <Page title='Login'>
//       <RootStyle>
//         <HeaderStyle>
//           <Logo sx={{ width: 200, height: 200 }} />
//           {smUp && (
//             <Typography variant='body2' sx={{ mt: { md: -2 } }}>
//               Don’t have an account? {''}
//               <Link variant='subtitle2' component={RouterLink} to={'mailto:info@buyzone.com'}>
//                 Contact The {BUSINESS_NAME} Team
//               </Link>
//             </Typography>
//           )}
//         </HeaderStyle>
//
//         {mdUp && (
//           <SectionStyle>
//             <Typography variant='h3' sx={{ px: 5, mt: 10, mb: 5 }}>
//               Hi, Welcome Back
//             </Typography>
//             <Image visibleByDefault disabledEffect alt='login' src='https://res.cloudinary.com/dgkpzukqz/image/upload/v1681222869/illustration_login.png' />
//           </SectionStyle>
//         )}
//
//         <Container maxWidth='sm'>
//           <ContentStyle>
//             <Stack direction='row' alignItems='center' sx={{ mb: 5 }}>
//               <Box sx={{ flexGrow: 1 }}>
//                 <Typography variant='h4' gutterBottom>
//                   Sign in to {BUSINESS_NAME},
//                 </Typography>
//                 <Typography sx={{ color: 'text.secondary' }}>Enter your credentials below.</Typography>
//               </Box>
//
//               <Tooltip title={capitalCase(method)} placement='right'>
//                 <>
//                   <Image disabledEffect src={`https://minimal-assets-api.vercel.app/assets/icons/auth/ic_${method}.png`} sx={{ width: 32, height: 32 }} />
//                 </>
//               </Tooltip>
//             </Stack>
//
//             <LoginForm />
//           </ContentStyle>
//         </Container>
//       </RootStyle>
//     </Page>
//   );
// }
